import { useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Collapse from 'react-bootstrap/Collapse';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputGroup from 'react-bootstrap/InputGroup';
import {
  faArrowUpRightFromSquare,
  faClipboardList,
  faChevronDown,
  faGraduationCap,
  faEnvelope,
  faBookOpen,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

const ENGAGEMENT_STATUSES = ['Not Activated', 'Awaiting HQ Access', 'Engaged', 'Disengaged'];

function stripEmojis(s) {
  if (!s) return '';
  return s.replace(/[^\p{L}\p{N}\s]/gu, '').trim();
}

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function maxDate(dates) {
  const valid = dates.filter(Boolean);
  if (valid.length === 0) return null;
  return new Date(Math.max(...valid.map((d) => d.getTime())));
}

function formatDateTime(d) {
  if (!d) return null;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffMs >= 0 && diffMin < 1) return 'Just now';
  if (diffMs >= 0 && diffMin < 60) return `${diffMin} min ago`;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfToday - startOfThat) / 86400000);
  if (dayDiff === 0) return `Today at ${time}`;
  if (dayDiff === 1) return `Yesterday at ${time}`;
  if (dayDiff > 1 && dayDiff < 7) {
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
    return `${weekday} at ${time}`;
  }
  if (d.getFullYear() === now.getFullYear()) {
    const md = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${md} at ${time}`;
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function lastLogin(enrollment) {
  return maxDate(
    (enrollment.course_enrollments || []).map((c) => parseDate(c.hq_last_login)),
  );
}

function lastInvite(enrollment) {
  return maxDate(
    (enrollment.course_enrollments || []).map((c) => parseDate(c.part_invite_sent_date_time)),
  );
}

function hqStudentLink(enrollment) {
  const userId = enrollment.contact?.canvas_user_id;
  if (!userId) return null;
  const partWithUrl = (enrollment.course_enrollments || []).find((c) => c.livelab?.hq_part_url);
  const partUrl = partWithUrl?.livelab?.hq_part_url;
  if (!partUrl) return null;
  try {
    const origin = new URL(partUrl).origin;
    return `${origin}/users/${userId}`;
  } catch {
    return null;
  }
}

const STATUS_STYLES = {
  'Engaged': { dot: '#198754', bg: '#d1e7dd', text: '#0a3622' },
  'Disengaged': { dot: '#dc3545', bg: '#f8d7da', text: '#58151c' },
  'Not Activated': { dot: '#fd7e14', bg: '#fff3cd', text: '#664d03' },
  'Awaiting HQ Access': { dot: '#6c757d', bg: '#e9ecef', text: '#495057' },
  'Unknown': { dot: '#adb5bd', bg: '#f8f9fa', text: '#6c757d' },
};

function lastName(e) {
  const name = (e.contact?.name || '').trim();
  if (!name) return '';
  const parts = name.split(/\s+/);
  return parts[parts.length - 1].toLowerCase();
}

const SORT_OPTIONS = {
  name: { label: 'Last name (A–Z)', value: lastName, dir: 'asc', type: 'string' },
  name_desc: { label: 'Last name (Z–A)', value: lastName, dir: 'desc', type: 'string' },
  status: { label: 'Engagement status', value: (e) => stripEmojis(e.engagement_status).toLowerCase(), dir: 'asc', type: 'string' },
  track: { label: 'Track', value: (e) => (e.program?.name || '').toLowerCase(), dir: 'asc', type: 'string' },
  recent_login: { label: 'Most recent HQ login', value: (e) => lastLogin(e)?.getTime() ?? null, dir: 'desc', type: 'number' },
};

function compareValues(a, b, type) {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (type === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function StatusPill({ status }) {
  const label = status || 'Unknown';
  const style = STATUS_STYLES[label] || STATUS_STYLES.Unknown;
  return (
    <span
      className="d-inline-flex align-items-center"
      style={{
        backgroundColor: style.bg,
        color: style.text,
        padding: '3px 10px',
        borderRadius: '999px',
        fontSize: '0.78rem',
        fontWeight: 500,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: style.dot,
          marginRight: '6px',
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

function StudentRow({ enrollment: e, isExpanded, onToggle }) {
  const status = stripEmojis(e.engagement_status);
  const link = hqStudentLink(e);
  const name = e.contact?.name || '—';
  const email = e.contact?.email;
  const track = e.program?.name;
  const codes = e.course_codes;

  return (
    <div className="border rounded-3 mb-2 bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="btn btn-link text-reset text-decoration-none w-100 text-start p-0"
        aria-expanded={isExpanded}
      >
        <div className="d-flex align-items-start gap-3 p-3">
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate" style={{ fontSize: '1rem' }}>
                {name}
              </span>
              <StatusPill status={status} />
            </div>
            {email && (
              <div className="text-muted small text-truncate mb-1">
                <FontAwesomeIcon icon={faEnvelope} className="me-2 opacity-50" />
                {email}
              </div>
            )}
            <div className="d-flex flex-wrap gap-3 small text-muted">
              {track && (
                <span className="text-truncate">
                  <FontAwesomeIcon icon={faGraduationCap} className="me-1 opacity-50" />
                  {track}
                </span>
              )}
              {codes && (
                <span className="text-truncate">
                  <FontAwesomeIcon icon={faBookOpen} className="me-1 opacity-50" />
                  {codes}
                </span>
              )}
            </div>
          </div>
          <div
            className="text-muted flex-shrink-0"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
            }}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </div>
        </div>
      </button>
      <Collapse in={isExpanded}>
        <div>
          <StudentDetails enrollment={e} link={link} />
        </div>
      </Collapse>
    </div>
  );
}

function StudentDetails({ enrollment: e, link }) {
  const login = formatDateTime(lastLogin(e));
  const invite = formatDateTime(lastInvite(e));
  const courses = e.course_enrollments || [];
  return (
    <div className="border-top bg-light p-3">
      <div className="row g-3 mb-3">
        <DetailField label="Last HQ login" value={login} fallback="Never" />
        <DetailField label="Invite sent" value={invite} />
        <DetailField label="Wave" value={e.wave?.wave} />
        <DetailField label="Section" value={e.section} />
        <DetailField label="Instructor of Record" value={e.instructor_of_record} />
        <div className="col-12 col-md-6">
          <div className="text-uppercase text-muted small fw-bold mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
            HQ Portal
          </div>
          {link ? (
            <a href={link} target="_blank" rel="noreferrer" className="text-decoration-none small">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-1" />
              Open student in HQ
            </a>
          ) : (
            <div className="text-muted small">No HQ link available</div>
          )}
        </div>
      </div>

      <div className="text-uppercase text-muted small fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
        Parts <Badge bg="secondary" pill className="ms-1">{courses.length}</Badge>
      </div>
      {courses.length === 0 ? (
        <div className="text-muted small">No parts on record.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {courses.map((c) => <CourseRow key={c.course_enrollment_id} course={c} />)}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, fallback }) {
  return (
    <div className="col-12 col-md-6">
      <div className="text-uppercase text-muted small fw-bold mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div className="small">
        {value || <span className="text-muted">{fallback || '—'}</span>}
      </div>
    </div>
  );
}

function CourseRow({ course: c }) {
  const status = c.withdrawn
    ? { text: 'Withdrawn', bg: 'danger' }
    : c.part_touched
      ? { text: 'Active', bg: 'success' }
      : { text: 'Not started', bg: 'secondary' };
  const invite = formatDateTime(parseDate(c.part_invite_sent_date_time));
  const activity = formatDateTime(parseDate(c.part_last_activity));
  return (
    <div className="bg-white border rounded-3 p-3">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="fw-semibold text-truncate">{c.livelab?.course_name || 'Course'}</div>
          <div className="text-muted small text-truncate">{c.livelab?.internal_long_code || '—'}</div>
        </div>
        <Badge bg={status.bg}>{status.text}</Badge>
      </div>
      <div className="row g-2 small">
        <CourseFact label="Invite sent" value={invite} />
        <CourseFact label="Last activity" value={activity} />
      </div>
    </div>
  );
}

function CourseFact({ label, value, fallback }) {
  return (
    <div className="col-12 col-sm-6">
      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
      <div>{value || <span className="text-muted">{fallback || '—'}</span>}</div>
    </div>
  );
}

export default function FullRoster({ university, pendingFilter }) {
  const [enrollments, setEnrollments] = useState(null);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [statusFilter, setStatusFilter] = useState('All');
  const [trackFilter, setTrackFilter] = useState('All');
  const [iorFilter, setIorFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    if (!pendingFilter) return;
    setStatusFilter(pendingFilter.status ?? 'All');
    setTrackFilter(pendingFilter.track ?? 'All');
    setIorFilter('All');
    setSearch('');
  }, [pendingFilter?.nonce]);

  useEffect(() => {
    if (!university?.university_id) return;
    setEnrollments(null);
    setError(null);
    const url = `/api/active_enrollments/${encodeURIComponent(university.university_id)}`;
    fetch(url, { headers: { Accept: 'application/vnd.howdy.active-enrollments+json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setEnrollments)
      .catch((err) => setError(err.message));
  }, [university?.university_id]);

  const instructors = useMemo(() => {
    const set = new Set();
    for (const e of enrollments || []) {
      if (e.instructor_of_record) set.add(e.instructor_of_record);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [enrollments]);

  const tracks = useMemo(() => {
    const set = new Set();
    for (const e of enrollments || []) {
      if (e.program?.name) set.add(e.program.name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [enrollments]);

  const countsByStatus = useMemo(() => {
    const counts = {};
    for (const opt of ENGAGEMENT_STATUSES) counts[opt] = 0;
    for (const e of enrollments || []) {
      const status = stripEmojis(e.engagement_status);
      if (status in counts) counts[status]++;
    }
    return counts;
  }, [enrollments]);

  const filteredSorted = useMemo(() => {
    if (!enrollments) return [];
    const opt = SORT_OPTIONS[sortKey];
    const factor = opt.dir === 'asc' ? 1 : -1;
    const q = search.trim().toLowerCase();
    return enrollments
      .filter((e) => statusFilter === 'All' || stripEmojis(e.engagement_status) === statusFilter)
      .filter((e) => trackFilter === 'All' || (e.program?.name || '') === trackFilter)
      .filter((e) => iorFilter === 'All' || (e.instructor_of_record || '') === iorFilter)
      .filter((e) => {
        if (!q) return true;
        const haystack = [
          e.contact?.name,
          e.contact?.email,
          e.program?.name,
          e.course_codes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice()
      .sort((a, b) => {
        const av = opt.value(a);
        const bv = opt.value(b);
        const aEmpty = av === null || av === undefined || av === '';
        const bEmpty = bv === null || bv === undefined || bv === '';
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        return factor * compareValues(av, bv, opt.type);
      });
  }, [enrollments, sortKey, statusFilter, trackFilter, iorFilter, search]);

  return (
    <Container className="py-4 py-md-5" style={{ maxWidth: '960px' }}>
      <div className="bg-white px-3 px-md-5 py-4 py-md-5 rounded-3 shadow-sm">
        <header className="mb-4">
          <h1
            className="fw-bold mb-2"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '1.75rem' }}
          >
            <FontAwesomeIcon icon={faClipboardList} className="text-primary me-2" />
            Student Full Roster
            {enrollments && (
              <span className="text-muted fw-normal"> ({enrollments.length})</span>
            )}
          </h1>
          <p className="text-muted mb-0" style={{ maxWidth: '620px' }}>
            Monitor engagement, pacing, participation, and program performance
            across all active students in the current term.
          </p>
        </header>

        <InputGroup className="mb-3">
          <InputGroup.Text className="bg-white">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="text-muted" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search by name, email, track, or course code"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            aria-label="Search roster"
          />
          {search && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </InputGroup>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <FilterChip
            label="All"
            count={enrollments?.length ?? 0}
            active={statusFilter === 'All'}
            onClick={() => setStatusFilter('All')}
          />
          {ENGAGEMENT_STATUSES.map((status) => (
            <FilterChip
              key={status}
              label={status}
              count={countsByStatus[status] ?? 0}
              active={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              dot={STATUS_STYLES[status]?.dot}
            />
          ))}
        </div>

        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <div className="d-flex align-items-center gap-2">
            <label
              htmlFor="roster-sort"
              className="text-muted small mb-0 flex-shrink-0"
              style={{ fontWeight: 500 }}
            >
              Sort by
            </label>
            <Form.Select
              id="roster-sort"
              size="sm"
              value={sortKey}
              onChange={(ev) => setSortKey(ev.target.value)}
              style={{ maxWidth: '260px' }}
            >
              {Object.entries(SORT_OPTIONS).map(([key, opt]) => (
                <option key={key} value={key}>{opt.label}</option>
              ))}
            </Form.Select>
          </div>
          {tracks.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <label
                htmlFor="roster-track"
                className="text-muted small mb-0 flex-shrink-0"
                style={{ fontWeight: 500 }}
              >
                Track
              </label>
              <Form.Select
                id="roster-track"
                size="sm"
                value={trackFilter}
                onChange={(ev) => setTrackFilter(ev.target.value)}
                style={{ maxWidth: '260px' }}
              >
                <option value="All">All tracks</option>
                {tracks.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Form.Select>
            </div>
          )}
          {instructors.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <label
                htmlFor="roster-ior"
                className="text-muted small mb-0 flex-shrink-0"
                style={{ fontWeight: 500 }}
              >
                Instructor
              </label>
              <Form.Select
                id="roster-ior"
                size="sm"
                value={iorFilter}
                onChange={(ev) => setIorFilter(ev.target.value)}
                style={{ maxWidth: '260px' }}
              >
                <option value="All">All instructors</option>
                {instructors.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Form.Select>
            </div>
          )}
        </div>

        {error && <Alert variant="danger">Failed to load roster: {error}</Alert>}

        {!error && !enrollments && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        )}

        {!error && enrollments && filteredSorted.length === 0 && (
          <div className="text-center text-muted py-5 border rounded-3 bg-light">
            No students match the current filters.
          </div>
        )}

        {!error && enrollments && filteredSorted.length > 0 && (
          <div>
            {filteredSorted.map((e, idx) => {
              const rowKey = e.program_enrollment_id || `idx-${idx}`;
              const isExpanded = expandedKey === rowKey;
              return (
                <StudentRow
                  key={rowKey}
                  enrollment={e}
                  isExpanded={isExpanded}
                  onToggle={() => setExpandedKey(isExpanded ? null : rowKey)}
                />
              );
            })}
          </div>
        )}
      </div>
    </Container>
  );
}

function FilterChip({ label, count, active, onClick, dot }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-sm d-inline-flex align-items-center gap-2 ${active ? 'btn-dark' : 'btn-outline-secondary'}`}
      style={{ borderRadius: '999px', fontSize: '0.82rem' }}
    >
      {dot && (
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: dot,
            display: 'inline-block',
          }}
        />
      )}
      <span>{label}</span>
      <Badge
        bg=""
        pill
        className={active ? 'text-bg-light text-dark' : 'text-bg-light text-secondary-emphasis border'}
        style={{ fontSize: '0.7rem', fontWeight: 500 }}
      >
        {count}
      </Badge>
    </button>
  );
}
